import { constVoid, pipe } from 'fp-ts/function';
import { Component, createRef, ReactNode } from 'react';
import styled from 'styled-components';
import { KeyValue } from '@mews/enums';
import { HeadingAction } from '../../legacy/React/Components/HeadingAction/Action/types';
import { TabsConfig } from '../../modules/modalManager';
import withKeyboardEventsHandler, { WithKeyboardEventHandler } from '../Hoc/withKeyboardEventsHandler';
import Sidebar, { CommanderSidebarProps } from './Sidebar';

export interface SidebarContentProps {
    actions?: HeadingAction[];
    showTitle?: boolean;
    sidebarDidClose?: () => void;
    title?: ReactNode;
    tabsConfig?: TabsConfig;
    withContentSpacing?: boolean;
    onClose?: () => void;
}

type GenericSidebarProps = CommanderSidebarProps & SidebarContentProps & WithKeyboardEventHandler;

const genericCommanderSidebarFactory = <P extends {}>(SidebarContent: React.ComponentType<SidebarContentProps & P>) =>
    class GenericSidebar extends Component<GenericSidebarProps & P> {
        sidebarElement = createRef<HTMLDivElement>();

        componentDidMount() {
            if (this.props.opened && this.sidebarElement) {
                this.props.subscribeKeyEvents(this.keyMap, this.sidebarElement.current);
            }
        }

        componentDidUpdate(prevProps: GenericSidebarProps) {
            if (prevProps.opened && !this.props.opened) {
                this.props.unsubscribeKeyEvents();
                this.props.sidebarDidClose?.();
            } else if (!prevProps.opened && this.props.opened && this.sidebarElement) {
                this.props.subscribeKeyEvents(this.keyMap, this.sidebarElement.current);
            }
        }

        componentWillUnmount() {
            this.props.unsubscribeKeyEvents();
        }
        keyMap = {
            [KeyValue.Escape]: {
                onEvent: this.props.onClose,
            },
        };

        render() {
            const {
                absolute,
                closeOnBackdropClick,
                disableClickAway,
                disableScroll,
                fullScreen,
                modal = false,
                modalIndex,
                onClose = constVoid,
                onClosingAnimationFinished,
                opened,
                showTitle = true,
                sidebarDidClose = constVoid,
                verticallyPadded,
                width,
                zIndex,
                ...other
            } = this.props;

            const sidebarProps = {
                absolute,
                closeOnBackdropClick,
                disableClickAway,
                disableScroll,
                fullScreen,
                modal,
                modalIndex,
                onClose,
                onClosingAnimationFinished,
                opened,
                verticallyPadded,
                width,
                zIndex,
            };

            const sidebarContentProps = {
                ...other,
                onClose,
                sidebarDidClose,
                showTitle,
            };

            return (
                <Sidebar {...sidebarProps}>
                    <SidebarContentWrapper ref={this.sidebarElement}>
                        {/* @ts-expect-error https://mews.myjetbrains.com/youtrack/issue/kanban-4230/Types-for-Commander-Sidebar */}
                        <SidebarContent {...sidebarContentProps} />
                    </SidebarContentWrapper>
                </Sidebar>
            );
        }
    };

/** @deprecated Use new Sidebar from DS vision */
export default <P extends {}>(SidebarContent: React.ComponentType<SidebarContentProps & P>) =>
    pipe(SidebarContent, genericCommanderSidebarFactory, withKeyboardEventsHandler);

export const SidebarContent = styled.div`
    flex-grow: 1;
    overflow-y: auto;
    overflow-x: hidden;
`;

export const SidebarContentWrapper = styled.div`
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    min-height: 0;
`;
